import './MembershipsPage.css';

import { useCallback, useMemo, useState } from 'react';
import ModuleNotEnabledState from '../../../shared/components/ModuleNotEnabledState';
import MembershipsHeader from '../components/MembershipsHeader';
import MembershipsTabs, { type MembershipsTabKey } from '../components/MembershipsTabs';
import TiersPanel from '../components/tiers/TiersPanel';
import MembersPanel from '../components/members/MembersPanel';
import ActionsPanel from '../components/actions/ActionsPanel';
import PendingMembershipsPanel from '../components/PendingMembershipsPanel';
import IssueMembershipModal from '../components/IssueMembershipModal';
import { usePendingMembershipRequests } from '../queries';
import { membershipApi, type MembershipMemberRow } from '../api/membership.api';
import { membersToCsvRows, membersCsvFilename } from '../utils/membershipCsv';
import { downloadCsv } from '../../analytics/utils/csvExport';
import Toast from '../../../shared/components/ui/Toast';

const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 100; // safety cap — up to 10k members

const TAB_VALUES: readonly MembershipsTabKey[] = ['members', 'tiers', 'activity', 'pending'];
const MEMBERSHIP_BASE_PATH = '/store/memberships';

const parseTab = (raw: string | null | undefined): MembershipsTabKey => {
  const value = String(raw || '').toLowerCase();
  return (TAB_VALUES as readonly string[]).includes(value)
    ? (value as MembershipsTabKey)
    : 'members';
};

const parseTabFromRoute = (routePath: string | undefined): MembershipsTabKey => {
  const [pathOnly, search = ''] = String(routePath || '').split('?');
  const segment = pathOnly.startsWith(`${MEMBERSHIP_BASE_PATH}/`)
    ? pathOnly.slice(MEMBERSHIP_BASE_PATH.length + 1).split('/')[0]
    : '';
  if ((TAB_VALUES as readonly string[]).includes(segment)) {
    return segment as MembershipsTabKey;
  }
  // Fallback: honour legacy ?tab=... so external bookmarks still land right.
  return parseTab(new URLSearchParams(search).get('tab'));
};

type MembershipsPageProps = {
  membershipEnabled: boolean;
  routePath?: string;
  onNavigate?: (path: string) => void;
};

/**
 * Stage 1 redesigned Memberships page (C1 scaffold).
 *
 * Renders the header + three-tab shell with placeholder panels. Real
 * content lands in subsequent phases:
 *   - C2/C3 → Tiers tab (KPIs, themed cards, benefits matrix)
 *   - D1/D2 → Members tab (table, drawer, issue-by-phone)
 *   - D3    → Actions tab (activity, expiring, upgrades, broadcast)
 */
const MembershipsPage = ({ membershipEnabled, routePath, onNavigate }: MembershipsPageProps) => {
  const initialTab = useMemo(() => parseTabFromRoute(routePath), [routePath]);
  const pendingQuery = usePendingMembershipRequests(membershipEnabled);
  const pendingCount = pendingQuery.data?.length ?? null;

  const issueRequested = useMemo(() => {
    const search = String(routePath || '').split('?')[1] || '';
    return new URLSearchParams(search).get('issue') === '1';
  }, [routePath]);

  // We deliberately key state to the URL so deep links survive reloads.
  // useState seed-only here; navigation handles URL sync.
  const activeTab = initialTab;

  const handleTabChange = useCallback(
    (next: MembershipsTabKey) => {
      if (next === activeTab) return;
      // Always re-root at /store/memberships — never inherit the prior path
      // segment, otherwise we'd end up at /store/memberships/members/tiers.
      const params = new URLSearchParams(routePath?.split('?')[1] || '');
      params.delete('tab');
      const query = params.toString();
      const path = `${MEMBERSHIP_BASE_PATH}/${next}`;
      onNavigate?.(query ? `${path}?${query}` : path);
    },
    [activeTab, onNavigate, routePath]
  );

  const handleNewTier = useCallback(() => onNavigate?.('/store/memberships/new'), [onNavigate]);

  const [isExporting, setIsExporting] = useState(false);
  const [toast, setToast] = useState<{ message: string; variant: string } | null>(null);

  // Export every member (all tiers, all statuses) to a branded CSV. Paginates
  // through the whole list since the API is paged.
  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const all: MembershipMemberRow[] = [];
      for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
        const res = await membershipApi.getMembersByType({ page, pageSize: EXPORT_PAGE_SIZE });
        const items = Array.isArray(res?.items) ? res.items : [];
        all.push(...items);
        if (!res?.hasNext || items.length === 0) break;
      }
      if (all.length === 0) {
        setToast({ message: 'No members to export.', variant: 'info' });
        return;
      }
      downloadCsv(membersCsvFilename(), membersToCsvRows(all), { title: 'Memberships' });
      setToast({
        message: `Exported ${all.length} member${all.length === 1 ? '' : 's'}.`,
        variant: 'success'
      });
    } catch (error) {
      setToast({ message: 'Could not export members. Please try again.', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [isExporting]);

  const [isIssueDrawerOpen, setIssueDrawerOpen] = useState(false);
  const handleCloseIssueDrawer = useCallback(() => setIssueDrawerOpen(false), []);

  // Issue Membership is the single canonical entry point on every tab.
  // Opens the modal inline at the page level — no navigation, no per-tab
  // forks. (MembersPanel still honours `?issue=1` deep links into its
  // phone-first sheet for backward compat with external links.)
  const handleIssueMembership = useCallback(() => {
    setIssueDrawerOpen(true);
  }, []);

  const handleEditTier = useCallback(
    (typeUuid: string) => onNavigate?.(`/store/memberships/${typeUuid}/edit`),
    [onNavigate]
  );

  const handleEditBenefits = useCallback(
    () => onNavigate?.('/store/memberships/new?step=3'),
    [onNavigate]
  );

  const handleOpenFullProfile = useCallback(
    (membershipUuid: string) => onNavigate?.(`/store/memberships/customers/${membershipUuid}`),
    [onNavigate]
  );

  const handleIssueSheetClose = useCallback(() => {
    if (!routePath || !routePath.includes('issue=1')) return;
    const params = new URLSearchParams(routePath.split('?')[1] || '');
    params.delete('issue');
    const base = routePath.split('?')[0];
    const next = params.toString() ? `${base}?${params.toString()}` : base;
    onNavigate?.(next);
  }, [onNavigate, routePath]);

  if (!membershipEnabled) {
    return (
      <div className="memberships-page">
        <ModuleNotEnabledState title="Memberships not enabled" />
      </div>
    );
  }

  return (
    <div className="memberships-page" data-testid="memberships-page-v2">
      <MembershipsHeader
        activeTab={activeTab}
        onExport={handleExport}
        exporting={isExporting}
        onNewTier={handleNewTier}
        onIssueMembership={handleIssueMembership}
      />
      <MembershipsTabs activeTab={activeTab} onChange={handleTabChange} pendingCount={pendingCount} />
      <section
        className="memberships-page__panel"
        role="tabpanel"
        aria-label={`${activeTab} panel`}
      >
        {activeTab === 'tiers' ? (
          <TiersPanel
            membershipEnabled={membershipEnabled}
            onCreateTier={handleNewTier}
            onIssueMembership={handleIssueMembership}
            onEditTier={handleEditTier}
            onEditBenefits={handleEditBenefits}
          />
        ) : null}
        {activeTab === 'members' ? (
          <MembersPanel
            membershipEnabled={membershipEnabled}
            onOpenFullProfile={handleOpenFullProfile}
            initialIssueOpen={issueRequested}
            onIssueSheetClose={handleIssueSheetClose}
          />
        ) : null}
        {activeTab === 'pending' ? <PendingMembershipsPanel /> : null}
        {activeTab === 'activity' ? <ActionsPanel /> : null}
      </section>
      <IssueMembershipModal
        open={isIssueDrawerOpen}
        onClose={handleCloseIssueDrawer}
      />
      {toast ? (
        <Toast
          message={toast.message}
          variant={toast.variant}
          duration={5000}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
};

export default MembershipsPage;
